/**
 * ErrorBoundary Component
 * 
 * A React error boundary that catches JavaScript errors anywhere in the
 * child component tree and displays a fallback UI instead of crashing
 * the entire application.
 * 
 * Features:
 * - Catches runtime errors in components
 * - Displays user-friendly error message
 * - Shows error details for debugging (development)
 * - Provides reload button to recover
 */

import React from 'react';
import { Button } from './ui/button';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render shows the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error for debugging
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ errorInfo });
    }

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-[#050505] text-white flex flex-col items-center justify-center p-6 text-center">
                    {/* Error Title */}
                    <h1 className="text-3xl text-[#D4AF37] font-serif mb-4">
                        Something went wrong
                    </h1>

                    {/* Error Description */}
                    <p className="text-white/60 mb-8 max-w-md">
                        We apologize for the inconvenience. The application encountered an unexpected error.
                    </p>

                    {/* Error Details (for debugging) */}
                    <div className="bg-[#0A0A0A] p-4 rounded border border-white/10 mb-8 text-left max-w-2xl w-full overflow-auto max-h-60">
                        <code className="text-red-400 font-mono text-sm block mb-2">
                            {this.state.error && this.state.error.toString()}
                        </code>
                        <pre className="text-white/40 font-mono text-xs whitespace-pre-wrap">
                            {this.state.errorInfo && this.state.errorInfo.componentStack}
                        </pre>
                    </div>

                    {/* Reload Button */}
                    <Button
                        className="bg-[#D4AF37] text-black hover:bg-[#B5952F]"
                        onClick={this.handleReload}
                    >
                        Reload Application
                    </Button>
                </div>
            );
        }

        // No error - render children normally
        return this.props.children;
    }
}

export default ErrorBoundary;
